import _ from 'lodash'
import { MutableRefObject } from 'react'
import { jsonResponseExtractor, requestCommon } from 'live/lib/utils'
import { defaultErrorHandler } from 'plugin-liverig/shared/asset/lib/utils'

export const redirectPathSvgPath = (svg: string): string =>
    svg.replaceAll('copcoipatterns', 'copcoi/patterns').replaceAll('/csb/assets/', '/content/plugin-liverig/')

export const getDefaultQuery = _.memoize(
    (eventType?: string): Promise<string> => {
        const payload = {
            kind: 'csb-chart',
            config: {
                span: 'last hour',
                kind: 'csb-chart',
                eventType: eventType || null
            }
        }

        return fetch('/rest/widget-query', {
            ...requestCommon,
            method: 'POST',
            body: JSON.stringify([payload])
        })
            .then(jsonResponseExtractor)
            .then(response => _.get(response, '[0].expression'))
            .catch(defaultErrorHandler)
    }
)

export const debouncedGetDefaultQuery = _.debounce(
    (evetType: string, callback: (exp: string) => void) => getDefaultQuery(evetType).then(callback),
    200
)

export const changeValvesColor = (containerRef: MutableRefObject<HTMLDivElement | undefined>, valves: Object[]) => {
    const closedStatus = ['CLOSED', 'FECHADO', 'FECHADA', 'FALSE']
    const openStatus = ['OPEN', 'ABERTO', 'ABERTA', 'TRUE']

    allValvesUndefined(containerRef)

    valves?.map((valveObj: Object) => {
        valveObj = _.mapKeys(valveObj, (valueStatus, keyValve) => keyValve.toUpperCase())

        Object.keys(valveObj).forEach((keyValve: string) => {
            const dhsv1status = valveObj['DHSV1_STATUS']?.toUpperCase()
            const dhsv2status = valveObj['DHSV2_STATUS']?.toUpperCase()

            // DHSV1 e DHSV2
            // somente quando as duas estiverem fechadas pintar de vermelho
            if (closedStatus.includes(dhsv1status) && closedStatus.includes(dhsv2status)) {
                valveObj['DHSV1_STATUS'] = 'CLOSED'
                valveObj['DHSV2_STATUS'] = 'CLOSED'
            }
            // DHSV1 e DHSV2
            // se uma das duas ou as duas estão aberta pintar de verde
            else if (openStatus.includes(dhsv1status) || openStatus.includes(dhsv2status)) {
                valveObj['DHSV1_STATUS'] = 'OPEN'
                valveObj['DHSV2_STATUS'] = 'OPEN'
            } else {
                valveObj['DHSV1_STATUS'] = 'UNDEFINED'
                valveObj['DHSV2_STATUS'] = 'UNDEFINED'
            }

            // A1 e A2
            // devem ter o mesmo status que a válvula M2
            // se a M2 estiver aberta pintar também as A1 e A2 de verde
            // se a M2 estiver fechada pintar as A1 e A2 de vermelho.
            if (keyValve === 'M2_STATUS') {
                valveObj['AI1_STATUS'] = valveObj['M2_STATUS']
                valveObj['AI2_STATUS'] = valveObj['M2_STATUS']
            }

            // S1 e S2
            // tem que ser pintadas de vermelho. E nao precisa ser animada
            valveObj['S1_STATUS'] = 'CLOSED'
            valveObj['S2_STATUS'] = 'CLOSED'
        })
        _.mapKeys(valveObj, (valueStatusValve: string, keyValve: string) => {
            defineValveColor(
                containerRef,
                _.get(valvesMap, keyValve),
                _.get(colorsObj, _.upperCase(valueStatusValve), colorsObj.UNDEFINED)
            )
        })
        defineDHSVColor(containerRef, _.get(colorsObj, _.upperCase(valveObj['DHSV1_STATUS']), colorsObj.UNDEFINED))
    })
    // const widgetMsg = containerRef.current?.parentNode?.parentNode?.parentNode?.parentNode?.querySelector('.widget-msg')
    // if (widgetMsg) widgetMsg.style.display = 'none'
}
export const setViewBoxOnSvg = (containerRef: MutableRefObject<HTMLDivElement | undefined>) => {
    // Fixes any svg without viewBox attribute
    const svg = containerRef?.current?.querySelector?.('svg')

    if (svg) {
        const svgWidth: number | null = _.parseInt(svg.getAttribute('width') ?? '') || null
        const svgHeight: number | null = _.parseInt(svg.getAttribute('height') ?? '') || null
        const svgViewBox = svg?.getAttribute('viewBox')
        if (!svgViewBox && _.isNumber(svgWidth) && _.isNumber(svgHeight)) {
            svg?.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`)
        }
    }
}

const colorsObj = {
    ABERTA: '#12AD12',
    ABERTO: '#12AD12',
    FECHADO: '#F70D0D',
    FECHADA: '#F70D0D',
    ON: '#12AD12',
    OPEN: '#12AD12',
    CLOSED: '#F70D0D',
    OFF: '#F70D0D',
    OPENING: '#D9F579',
    CLOSING: '#F7CE72',
    TRUE: '#12AD12',
    FALSE: '#F70D0D',
    UNDEFINED: '#b5a9a9'
}

export const valvesMap = {
    M1_STATUS: 'wt-valve-m1',
    M2_STATUS: 'wt-valve-m2',
    W1_STATUS: 'wt-valve-w1',
    W2_STATUS: 'wt-valve-w2',
    XO_STATUS: 'wt-valve-xo',
    DHSV1_STATUS: 'wt-valve-dhsv1',
    DHSV2_STATUS: 'wt-valve-dhsv2',
    AI1_STATUS: 'wt-valve-ai1',
    AI2_STATUS: 'wt-valve-ai2',
    VDV_STATUS: 'wt-valve-vdv',
    BAP_WITHOUT_ANNULAR1_STATUS: 'wt-valve-bap-without-annular1',
    BAP_WITHOUT_ANNULAR2_STATUS: 'wt-valve-bap-without-annular2',
    S1_STATUS: 'wt-valve-s1',
    S2_STATUS: 'wt-valve-s2',
    UM_STATUS: 'wt-valve-um',
    LM_STATUS: 'wt-valve-lm'
}

const allValvesUndefined = (containerRef: MutableRefObject<HTMLDivElement | undefined>) => {
    for (const valveIdentifier in valvesMap) {
        if (valvesMap.hasOwnProperty(valveIdentifier)) {
            const valveSelector = valvesMap[valveIdentifier]
            defineValveColor(containerRef, valveSelector, colorsObj.UNDEFINED)
        }
    }
    defineDHSVColor(containerRef, colorsObj.UNDEFINED)
}

const defineValveColor = (
    containerRef: MutableRefObject<HTMLDivElement | undefined>,
    valveSelector: string,
    fillColor: string
) => {
    const valveCircleRef: HTMLElement | null | undefined =
        containerRef?.current?.querySelector?.(`#${valveSelector} > circle`) ||
        containerRef?.current?.querySelector?.(`#${valveSelector}`)

    if (containerRef?.current && valveCircleRef) {
        valveCircleRef.style.fill = ''
        valveCircleRef?.setAttribute('fill', fillColor)
    }
}

const defineDHSVColor = (containerRef: MutableRefObject<HTMLDivElement | undefined>, fillColor: string) => {
    containerRef?.current?.querySelectorAll?.('#barrier_8_svg path:first-child, #wt-valve-dhsv').forEach(element => {
        element.style.fill = fillColor
        element.setAttribute('fill', fillColor)
    })
}
